import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAuth } from '../hooks/useAuth';
import MetricCard from '../components/analytics/MetricCard';
import MonthlyBarChart from '../components/analytics/MonthlyBarChart';
import CategoryPieChart from '../components/analytics/CategoryPieChart';
import OutlierCard from '../components/analytics/OutlierCard';
import {
  getYearlySummary,
  getCategoryBreakdown,
  getCategoryDrilldown,
  getTrends,
  getOutliers,
  getAvailableYears,
} from '../services/analytics';

const formatCurrency = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000) {
    return `$${(abs / 1000).toFixed(1)}k`;
  }
  return `$${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatCurrencyFull = (value) =>
  `$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [year, setYear] = useState(null);
  const [years, setYears] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [drilldownCategories, setDrilldownCategories] = useState([]);
  const [trends, setTrends] = useState([]);
  const [outliers, setOutliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async (selectedYear) => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, catData, drilldownData, trendData, outlierData] = await Promise.all([
        getYearlySummary(selectedYear),
        getCategoryBreakdown(selectedYear),
        getCategoryDrilldown(selectedYear),
        getTrends(selectedYear),
        getOutliers(selectedYear),
      ]);
      setSummary(summaryData);
      setCategories(catData);
      setDrilldownCategories(drilldownData);
      setTrends(trendData);
      setOutliers(outlierData);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError('Failed to load analytics data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const availableYears = await getAvailableYears();
        setYears(availableYears);
        const defaultYear = availableYears.length > 0 ? availableYears[0] : null;
        setYear(defaultYear);
        await loadData(defaultYear);
      } catch (err) {
        if (err.response?.status !== 401) {
          setLoading(false);
          // No data yet — show empty state
        }
      }
    }
    init();
  }, [loadData]);

  const handleYearChange = (e) => {
    const newYear = e.target.value || null;
    setYear(newYear);
    loadData(newYear);
  };

  const hasData = summary && summary.months && summary.months.length > 0;

  // Empty state — no data uploaded yet
  if (!loading && !hasData) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Welcome, {user?.username}!
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Upload your Excel budget files to see spending analytics, trends, and insights.
        </Alert>
        <Button
          variant="contained"
          size="large"
          startIcon={<CloudUploadIcon />}
          onClick={() => navigate('/upload')}
        >
          Upload Budget Files
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {years.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year || ''} label="Year" onChange={handleYearChange}>
                <MenuItem value="">All Years</MenuItem>
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/analytics')}
          >
            Detailed Analytics
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Summary metric cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Income"
                value={formatCurrency(summary.totals.income)}
                subtitle={`Avg ${formatCurrencyFull(summary.averages.income)}/mo`}
                icon={<TrendingUpIcon sx={{ fontSize: 32 }} />}
                color="secondary.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Expenses"
                value={formatCurrency(summary.totals.expenses)}
                subtitle={`Avg ${formatCurrencyFull(summary.averages.expenses)}/mo`}
                icon={<TrendingDownIcon sx={{ fontSize: 32 }} />}
                color="error.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Net Cash Flow"
                value={formatCurrency(summary.totals.net)}
                subtitle={`Avg ${formatCurrencyFull(summary.averages.net)}/mo`}
                icon={<AccountBalanceIcon sx={{ fontSize: 32 }} />}
                color={summary.totals.net >= 0 ? 'secondary.main' : 'error.main'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Months Tracked"
                value={summary.num_months}
                subtitle={`${categories.length} categories`}
                icon={<ReceiptLongIcon sx={{ fontSize: 32 }} />}
                color="primary.main"
              />
            </Grid>
          </Grid>

          {/* Charts */}
          <Box sx={{ mb: 3 }}>
            <MonthlyBarChart data={summary.months} />
          </Box>
          <Box sx={{ mb: 3 }}>
            <CategoryPieChart data={categories} drilldownData={drilldownCategories} />
          </Box>

          {/* Insights */}
          <OutlierCard trends={trends} outliers={outliers} />
        </>
      )}
    </Box>
  );
}
